import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Listen,
  Method,
  Prop,
  State,
  getAssetPath,
  h,
} from '@stencil/core';
import { getAPI, type OrganizationAPI, type UIApiConfig, type UserAPI } from '@sydle/ui-api';
import { getConfig } from '@sydle/ui-utils';
import { syQuerySelector } from '../../../utils/dom.utils';

@Component({
  tag: 'sy-blog-sign-in-wall',
  styleUrl: './scss/index.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SydleBlogSignInWall {
  @Element() element: HTMLSyBlogSignInWallElement;
  private socialMediaButtons?: HTMLElement;
  private dialog: HTMLSyDialogElement;

  @State() emailTokenInputValue: any = null;
  @State() authenticators: any;

  /**
   * Privacy policy text in HTML format.
   */
  @Prop() policy: string;

  /**
   * Title message for the sign-in wall.
   */
  @Prop() msgTitle: string;

  /**
   * Text of the email receivement
   */
  @Prop() emailReceivement: string;

  /**
   * List of supported social networks for sign-in.
   */
  @Prop() supportedNetworks: any[];

  /**
   * Emitted when the sign in wall is closed
   */
  @Event({ bubbles: true, composed: true }) sySignInWallDidClose: EventEmitter<void>;

  @Listen('sySignInWallOpen')
  onSySignInWallOpen(): void {
    this.open();
  }

  /**
   * Opens the login dialog
   */
  @Method()
  async open(): Promise<void> {
    this.dialog.open();
  }

  /**
   * Closes the login dialog
   */
  @Method()
  async close(): Promise<void> {
    this.dialog.close();
    this.sySignInWallDidClose.emit();
  }

  private loginWithRedirect(dataLogin: any): void {
    const URLI_pos_redirect = window.location.origin + dataLogin.redirect;
    const url = `${dataLogin.auth.loginUrl}?one_redirect=${URLI_pos_redirect}`;
    window.localStorage['providerSignIn'] = dataLogin.identifier;
    window.location.assign(url);
  }

  private submitHandler(auth: any): void {
    const obj_url = new URL(window.location.href);
    const queryParams = new URLSearchParams(obj_url.search);
    const redirect = queryParams.get('redirect') || window.location.pathname;
    this.loginWithRedirect({ ...auth, redirect });
  }

  private handleSocialLogin = (socialNetwork: string) => {
    this.socialMediaButtons
      ?.querySelectorAll(`.btn-${socialNetwork} .button-text`)
      .forEach((el: HTMLElement) => (el.style.display = 'none'));
    this.socialMediaButtons
      ?.querySelectorAll(`.btn-${socialNetwork} .sy-loader-ellipsis`)
      .forEach((el: HTMLElement) => (el.style.display = 'inline-block'));
    this.submitHandler({
      auth: this.supportedNetworks.find((authenticator) => authenticator.social.includes(socialNetwork)),
    });
  };

  private elipseLoader(style = { display: 'none' }) {
    return (
      <div class="sy-loader-ellipsis" style={style}>
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    );
  }

  private getSupportedNetworks() {
    const networks = this.authenticators.reduce((acc, authenticator) => {
      if (authenticator.identifier.toLowerCase().includes('google')) {
        acc.push({ ...authenticator, social: 'google' });
      } else if (authenticator.identifier.toLowerCase().includes('facebook')) {
        acc.push({ ...authenticator, social: 'facebook' });
      } else if (authenticator.identifier.toLowerCase().includes('linkedin')) {
        acc.push({ ...authenticator, social: 'linkedin' });
      }
      return acc;
    }, []);
    return networks.length > 0 ? networks : null;
  }

  private handleOutsideClick(event: Event, outsideEl: HTMLElement | undefined) {
    if (event.target === outsideEl) {
      event?.stopImmediatePropagation();
      this.close();
    }
  }

  private generateSocialButton(network, onClickHandler, assetPath, buttonText) {
    return (
      <sy-button variant="filled" color="secondary" onClick={() => onClickHandler(network)} class={`btn-${network}`}>
        <div class="button-content">
          <div class="button-icon">
            <img src={getAssetPath(assetPath)} alt={network} />
          </div>
          <div class="absolute-content">
            <sy-text class="button-text" type="body">
              {buttonText.charAt(0).toUpperCase() + buttonText.slice(1)}
            </sy-text>
            {this.elipseLoader()}
          </div>
        </div>
      </sy-button>
    );
  }

  private signInWallOpen = () => {
    if (!getAPI<UserAPI>('userAPI')?.getUser()) {
      (syQuerySelector('sy-blog-sign-in-wall') as HTMLSyBlogSignInWallElement).open();
    }
  };

  async componentWillLoad() {
    this.authenticators = await getAPI<OrganizationAPI>('organizationAPI')
      ?.getConfigs({
        _id: getConfig<UIApiConfig>('ui-api')?.ORGANIZATION_IDENTIFIER || '',
      })
      .then((response) => response.data?.authenticatorsConfig);

    this.supportedNetworks = this.getSupportedNetworks();
  }

  disconnectedCallback() {
    document.body?.removeEventListener('sySignInWallOpen', this.signInWallOpen);
  }

  connectedCallback() {
    document.body?.addEventListener('sySignInWallOpen', this.signInWallOpen);
  }

  render() {
    return (
      <Host>
        <sy-dialog
          ref={(el: HTMLSyDialogElement) => (this.dialog = el)}
          hide-footer
          hide-close
          onSyDialogDidClose={() => {
            this.close();
          }}
          onClick={(e) => this.handleOutsideClick(e, this.dialog)}
        >
          <div class="social-media-overlay">
            <sy-button
              onClick={() => this.close()}
              class="close"
              content="icon-only"
              label="Button"
              icon="close"
              type="basic"
              variant="filled"
              color="secondary"
              target="_blank"
              button-type="button"
            ></sy-button>
            <div class="title">
              <sy-text type="h2">{this.msgTitle}</sy-text>
            </div>

            {this.supportedNetworks ? (
              <div ref={(el) => (this.socialMediaButtons = el as HTMLElement)} class="social-media-buttons">
                {this.supportedNetworks.map((network) =>
                  this.generateSocialButton(
                    network.social,
                    this.handleSocialLogin,
                    `./assets/icon-login-${network.social}.svg`,
                    network.social,
                  ),
                )}
              </div>
            ) : null}

            <div class="content">
              <slot name="content">
                <div class="privacy-policy">
                  <div class="terms-and-conditions">
                    <sy-text type="small">{this.emailReceivement}</sy-text>
                  </div>
                  <div>
                    <sy-text type="small" innerHTML={this.policy}></sy-text>
                  </div>
                </div>
              </slot>
            </div>
          </div>
          <div style={{ display: 'none' }}>
            <slot></slot>
          </div>
        </sy-dialog>
      </Host>
    );
  }
}
